import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getAccessToken, onAccessTokenChange } from "../lib/api";
import { socket } from "../lib/socket";
import type {
  AckResponse,
  DrawSource,
  GameState,
  JoinData,
  ProtocolError,
  RoomClosed,
  RoomState,
  StoredSession,
} from "../types/protocol";

const SESSION_KEY = "skyjo.session.v1";
const NAME_KEY = "skyjo.player-name.v1";
const LEGACY_SESSION_KEY = "astra.session.v1";
const LEGACY_NAME_KEY = "astra.player-name.v1";
const ACK_TIMEOUT = 9_000;
const LEAVE_ACK_TIMEOUT = 1_000;

export type ConnectionState = "connecting" | "connected" | "reconnecting" | "offline";

const readSession = (): StoredSession | null => {
  try {
    const raw = localStorage.getItem(SESSION_KEY) ?? localStorage.getItem(LEGACY_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredSession> & { resumeOnly?: unknown };
    // Quitter la table libérait autrefois la place tout en gardant de quoi
    // « reprendre la partie » : la reprise ne pouvait qu'échouer, puisque le
    // siège était rendu. Ces sessions-là s'effacent au premier passage.
    if (parsed.resumeOnly === true) {
      writeSession(null);
      return null;
    }
    if (
      typeof parsed.roomCode !== "string" ||
      typeof parsed.playerId !== "string" ||
      typeof parsed.name !== "string" ||
      typeof parsed.reconnectToken !== "string"
    ) {
      return null;
    }
    return parsed as StoredSession;
  } catch {
    return null;
  }
};

const writeSession = (session: StoredSession | null) => {
  try {
    if (session) localStorage.setItem(SESSION_KEY, JSON.stringify(session));
    else localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem(LEGACY_SESSION_KEY);
  } catch {
    // L'application reste utilisable si le stockage privé est indisponible.
  }
};

const readPreferredName = () => {
  try {
    return localStorage.getItem(NAME_KEY) ?? localStorage.getItem(LEGACY_NAME_KEY) ?? "";
  } catch {
    return "";
  }
};

const writePreferredName = (name: string) => {
  try {
    localStorage.setItem(NAME_KEY, name);
    localStorage.removeItem(LEGACY_NAME_KEY);
  } catch {
    // Non bloquant.
  }
};

const timeoutError: ProtocolError = {
  code: "ACK_TIMEOUT",
  message: "Le serveur met trop de temps à répondre. Réessaie dans un instant.",
};

/** Le serveur impose le pseudo du compte : il fait foi sur celui qu'on a saisi. */
const seatedName = (data: JoinData, fallback: string) =>
  data.room.players.find((player) => player.id === data.playerId)?.name ?? fallback;

export interface SkyjoGameClient {
  room: RoomState | null;
  game: GameState | null;
  connection: ConnectionState;
  pendingAction: string | null;
  error: ProtocolError | null;
  preferredName: string;
  isRestoring: boolean;
  createRoom: (name: string) => Promise<boolean>;
  joinRoom: (code: string, name: string) => Promise<boolean>;
  /** Rejoint la table d'une invitation déjà acceptée auprès du serveur. */
  acceptInvite: (data: JoinData) => void;
  /**
   * Quitte la table pour de bon. En partie la place est perdue : le tour de
   * rôle enjambe le siège vide et le serveur refusera de l'y rasseoir.
   */
  leaveRoom: () => Promise<void>;
  /** Réservé à l'hôte : dissout la table pour tout le monde. */
  closeRoom: () => Promise<boolean>;
  startGame: () => Promise<boolean>;
  revealInitial: (positions: [number, number]) => Promise<boolean>;
  draw: (source: DrawSource) => Promise<boolean>;
  swap: (index: number) => Promise<boolean>;
  discardDrawn: () => Promise<boolean>;
  reclaimDiscard: () => Promise<boolean>;
  revealAfterDiscard: (index: number) => Promise<boolean>;
  nextRound: () => Promise<boolean>;
  dismissError: () => void;
  goHome: () => void;
}

export const useSkyjoGame = (): SkyjoGameClient => {
  const [room, setRoom] = useState<RoomState | null>(null);
  const [game, setGame] = useState<GameState | null>(null);
  const [connection, setConnection] = useState<ConnectionState>("connecting");
  const [pendingAction, setPendingAction] = useState<string | null>(null);
  const [error, setError] = useState<ProtocolError | null>(null);
  const [isRestoring, setIsRestoring] = useState(() => readSession() !== null);
  const [preferredName, setPreferredName] = useState(readPreferredName);
  const restoreInFlight = useRef(false);
  const restoreTimer = useRef<number | null>(null);
  const restoreGeneration = useRef(0);
  const ackActionLock = useRef(false);
  const cancelPendingAckRef = useRef<(() => void) | null>(null);

  const cancelRestore = useCallback(() => {
    restoreGeneration.current += 1;
    restoreInFlight.current = false;
    if (restoreTimer.current !== null) {
      window.clearTimeout(restoreTimer.current);
      restoreTimer.current = null;
    }
  }, []);

  const acceptJoin = useCallback((data: JoinData, name: string) => {
    setRoom(data.room);
    setGame(data.game ?? null);
    setError(null);
    const session: StoredSession = {
      roomCode: data.room.code,
      playerId: data.playerId,
      name,
      reconnectToken: data.reconnectToken,
    };
    writeSession(session);
    writePreferredName(name);
    setPreferredName(name);
  }, []);

  const fail = useCallback((protocolError: ProtocolError) => {
    setError(protocolError);
    return false;
  }, []);

  const restoreSession = useCallback(() => {
    const session = readSession();
    if (!session || restoreInFlight.current) {
      setIsRestoring(false);
      return;
    }

    restoreInFlight.current = true;
    const generation = ++restoreGeneration.current;
    setIsRestoring(true);
    restoreTimer.current = window.setTimeout(() => {
      if (generation !== restoreGeneration.current) return;
      restoreInFlight.current = false;
      restoreTimer.current = null;
      setIsRestoring(false);
      setConnection(socket.connected ? "connected" : "offline");
      setError(timeoutError);
    }, ACK_TIMEOUT);

    socket.emit(
      "room:join",
      {
        code: session.roomCode,
        name: session.name,
        reconnectToken: session.reconnectToken,
      },
      (response) => {
        if (generation !== restoreGeneration.current) return;
        if (restoreTimer.current !== null) window.clearTimeout(restoreTimer.current);
        restoreTimer.current = null;
        restoreInFlight.current = false;
        setIsRestoring(false);
        if (response.ok) {
          acceptJoin(response.data, session.name);
          return;
        }
        writeSession(null);
        setRoom(null);
        setGame(null);
        setError({
          code: response.error.code,
          message: "Ta session a expiré. Tu peux rejoindre à nouveau avec le code du salon.",
          details: response.error.details,
        });
      },
    );
  }, [acceptJoin]);

  useEffect(() => {
    const onConnect = () => {
      setConnection("connected");
      restoreSession();
    };
    const onDisconnect = () => {
      cancelRestore();
      setIsRestoring(readSession() !== null);
      setConnection(socket.active ? "reconnecting" : "offline");
    };
    const onConnectError = () => {
      setConnection(socket.active ? "reconnecting" : "offline");
      setIsRestoring(false);
    };
    const onRoomState = (nextRoom: RoomState) => setRoom(nextRoom);
    const onGameState = (nextGame: GameState) => setGame(nextGame);
    const onGameError = (nextError: ProtocolError) => setError(nextError);
    /**
     * La table a disparu sous les pieds du joueur. Aucun `room:state` ne
     * viendra plus : sans ce nettoyage il resterait devant un plateau figé,
     * et l'offre de reprise pointerait vers un salon qui n'existe plus.
     */
    const onRoomClosed = (payload: RoomClosed) => {
      cancelPendingAckRef.current?.();
      cancelRestore();
      writeSession(null);
      setRoom(null);
      setGame(null);
      setPendingAction(null);
      setIsRestoring(false);
      setError({
        code: "ROOM_CLOSED",
        message:
          payload.reason === "host-closed"
            ? "L’hôte a fermé la table."
            : "La table s’est refermée : il n’y restait plus personne.",
      });
    };

    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    socket.on("connect_error", onConnectError);
    socket.on("room:state", onRoomState);
    socket.on("game:state", onGameState);
    socket.on("game:error", onGameError);
    socket.on("room:closed", onRoomClosed);

    setConnection(socket.connected ? "connected" : "connecting");
    if (socket.connected) restoreSession();
    else socket.connect();

    return () => {
      cancelRestore();
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      socket.off("connect_error", onConnectError);
      socket.off("room:state", onRoomState);
      socket.off("game:state", onGameState);
      socket.off("game:error", onGameError);
      socket.off("room:closed", onRoomClosed);
    };
  }, [cancelRestore, restoreSession]);

  // Le jeton part déjà dans la poignée de main : ce relais ne couvre que les
  // changements survenus sur une connexion ouverte (connexion, déconnexion,
  // rafraîchissement silencieux).
  useEffect(
    () =>
      onAccessTokenChange((token) => {
        if (!socket.connected) return;
        if (token) socket.emit("auth:identify", { accessToken: token }, () => {});
        else socket.emit("auth:logout", () => {});
      }),
    [],
  );

  const runAck = useCallback(
    <T,>(
      action: string,
      emit: (ack: (response: AckResponse<T>) => void) => void,
      onSuccess?: (data: T) => void,
    ): Promise<boolean> => {
      if (ackActionLock.current) return Promise.resolve(false);
      ackActionLock.current = true;
      setPendingAction(action);
      setError(null);
      return new Promise((resolve) => {
        let finished = false;
        let cancelPendingAck: () => void;
        const complete = (result: boolean) => {
          if (finished) return;
          finished = true;
          window.clearTimeout(timer);
          if (cancelPendingAckRef.current === cancelPendingAck) {
            cancelPendingAckRef.current = null;
          }
          ackActionLock.current = false;
          setPendingAction(null);
          resolve(result);
        };
        cancelPendingAck = () => complete(false);
        const timer = window.setTimeout(() => complete(fail(timeoutError)), ACK_TIMEOUT);
        cancelPendingAckRef.current = cancelPendingAck;
        emit((response) => {
          if (finished) return;
          if (!response.ok) {
            complete(fail(response.error));
            return;
          }
          onSuccess?.(response.data);
          complete(true);
        });
      });
    },
    [fail],
  );

  const createRoom = useCallback(
    async (rawName: string) => {
      const name = rawName.trim();
      // Un compte connecté joue sous son pseudo : on laisse le serveur trancher.
      const identity = getAccessToken() ? {} : { name };
      return runAck<JoinData>(
        "create-room",
        (ack) => socket.emit("room:create", identity, ack),
        (data) => acceptJoin(data, seatedName(data, name)),
      );
    },
    [acceptJoin, runAck],
  );

  const joinRoom = useCallback(
    async (rawCode: string, rawName: string) => {
      const code = rawCode.trim().toUpperCase();
      const name = rawName.trim();
      const stored = readSession();
      const reconnectToken = stored?.roomCode === code ? stored.reconnectToken : undefined;
      const identity = getAccessToken() ? {} : { name };
      return runAck<JoinData>(
        "join-room",
        (ack) => socket.emit("room:join", { code, ...identity, reconnectToken }, ack),
        (data) => acceptJoin(data, seatedName(data, name)),
      );
    },
    [acceptJoin, runAck],
  );

  const acceptInvite = useCallback(
    (data: JoinData) => acceptJoin(data, seatedName(data, readPreferredName())),
    [acceptJoin],
  );

  const startGame = useCallback(
    () => runAck("start-game", (ack) => socket.emit("game:start", ack)),
    [runAck],
  );

  const revealInitial = useCallback(
    (positions: [number, number]) =>
      runAck("initial-reveal", (ack) => socket.emit("game:reveal-initial", { positions }, ack)),
    [runAck],
  );

  const draw = useCallback(
    (source: DrawSource) =>
      runAck(`draw-${source}`, (ack) => socket.emit("game:draw", { source }, ack)),
    [runAck],
  );

  const swap = useCallback(
    (index: number) => runAck("swap", (ack) => socket.emit("game:swap", { index }, ack)),
    [runAck],
  );

  const discardDrawn = useCallback(
    () => runAck("discard-drawn", (ack) => socket.emit("game:discard-drawn", ack)),
    [runAck],
  );

  const reclaimDiscard = useCallback(
    () => runAck("reclaim-discard", (ack) => socket.emit("game:reclaim-discard", ack)),
    [runAck],
  );

  const revealAfterDiscard = useCallback(
    (index: number) =>
      runAck("reveal-after-discard", (ack) =>
        socket.emit("game:reveal-after-discard", { index }, ack),
      ),
    [runAck],
  );

  const nextRound = useCallback(
    () => runAck("next-round", (ack) => socket.emit("game:next-round", ack)),
    [runAck],
  );

  const dismissError = useCallback(() => setError(null), []);

  /**
   * L'hôte dissout la table. Le serveur renvoie `room:closed` à tout le monde,
   * hôte compris : le nettoyage local se fait donc là-bas, en un seul endroit.
   */
  const closeRoom = useCallback(
    () => runAck("close-room", (ack) => socket.emit("room:close", ack)),
    [runAck],
  );

  /**
   * Le serveur refuse d'asseoir une connexion déjà attablée : il faut donc
   * quitter avant d'enchaîner sur une autre table. On garde la socket ouverte,
   * sinon le compte serait détaché et l'invitation ne pourrait plus être
   * consommée.
   *
   * Le départ est définitif, y compris au milieu d'une partie : la session
   * s'efface, faute de quoi l'accueil proposerait de reprendre un siège que le
   * serveur ne rend plus.
   */
  const leaveRoom = useCallback(
    () =>
      new Promise<void>((resolve) => {
        cancelPendingAckRef.current?.();
        cancelRestore();
        writeSession(null);
        setRoom(null);
        setGame(null);
        setError(null);
        setPendingAction(null);
        setIsRestoring(false);

        if (room === null || !socket.connected) {
          resolve();
          return;
        }

        let settled = false;
        const finish = () => {
          if (settled) return;
          settled = true;
          window.clearTimeout(timer);
          resolve();
        };
        // Un serveur muet ne doit pas bloquer le joueur : on enchaîne quand même.
        const timer = window.setTimeout(finish, LEAVE_ACK_TIMEOUT);
        socket.emit("room:leave", finish);
      }),
    [cancelRestore, room],
  );

  /**
   * Retour à l'accueil par le bouton de sortie : c'est un départ, pas une mise
   * en attente. Le serveur libère la place — en partie il retire le joueur du
   * tour de rôle — donc la session locale s'efface avec elle.
   */
  const goHome = useCallback(() => {
    cancelPendingAckRef.current?.();
    cancelRestore();
    writeSession(null);

    const notifyServer = socket.connected && room !== null;
    let leaveTimer: number | null = null;
    let recycled = false;
    const recycleSocket = () => {
      if (recycled) return;
      recycled = true;
      if (leaveTimer !== null) window.clearTimeout(leaveTimer);
      socket.disconnect();
      setConnection("connecting");
      socket.connect();
    };

    if (notifyServer) {
      leaveTimer = window.setTimeout(recycleSocket, LEAVE_ACK_TIMEOUT);
      socket.emit("room:leave", recycleSocket);
    } else {
      recycleSocket();
    }

    setRoom(null);
    setGame(null);
    setError(null);
    setPendingAction(null);
    setIsRestoring(false);
    setConnection("connecting");
  }, [cancelRestore, room]);

  return useMemo(
    () => ({
      room,
      game,
      connection,
      pendingAction,
      error,
      preferredName,
      isRestoring,
      createRoom,
      joinRoom,
      acceptInvite,
      leaveRoom,
      closeRoom,
      startGame,
      revealInitial,
      draw,
      swap,
      discardDrawn,
      reclaimDiscard,
      revealAfterDiscard,
      nextRound,
      dismissError,
      goHome,
    }),
    [
      acceptInvite,
      closeRoom,
      connection,
      createRoom,
      discardDrawn,
      dismissError,
      draw,
      error,
      game,
      goHome,
      isRestoring,
      joinRoom,
      leaveRoom,
      nextRound,
      pendingAction,
      preferredName,
      reclaimDiscard,
      room,
      revealInitial,
      revealAfterDiscard,
      startGame,
      swap,
    ],
  );
};
